import { useCallback, useMemo } from 'react';
import { useOkapiKy } from '@folio/stripes/core';
import { useQuery } from 'react-query';
var SYSTEM_USER_IDS = {
    '00000000-0000-0000-0000-000000000000': 'System',
    '00000000-0000-0000-0000-000000000001': 'Import',
};
var USER_NAMES_PATH = 'czna-users/users/names';
var STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes — mirrors useOrganizationNames
/**
 * Batched form of `useUserName` (CZNA-1100): a proposal report attributing up to a
 * thousand rows to their creators in one render otherwise fires one `/name` request per
 * distinct id — exactly the shape {@link useOrganizationNames} already avoids for
 * libraries. Unlike organizations there is no bounded "everyone" collection to fetch
 * once, so the caller passes exactly the ids it needs and this resolves only those.
 *
 * @param ids every `createdBy` id the caller is about to render; duplicates and system
 *   ids are filtered out before the request is built.
 */
var useUserNames = function (ids) {
    var ky = useOkapiKy();
    var idsToFetch = useMemo(function () { return Array.from(new Set(ids.filter(function (id) { return !!id && !SYSTEM_USER_IDS[id]; }))).sort(); }, [ids]);
    var _a = useQuery(['czna-users-user-names', idsToFetch], function () { return ky.post(USER_NAMES_PATH, { json: { ids: idsToFetch } }).json(); }, {
        enabled: idsToFetch.length > 0,
        staleTime: STALE_TIME_MS,
        // Same degrade-to-unresolved reasoning as useOrganizationNames: one user this
        // request can't resolve must not block the rest of the report from rendering.
        retry: false,
    }), data = _a.data, isLoading = _a.isLoading;
    var namesById = useMemo(function () {
        var map = new Map();
        (data !== null && data !== void 0 ? data : []).forEach(function (user) {
            if (user === null || user === void 0 ? void 0 : user.id) {
                map.set(user.id, "".concat(user.firstName, " ").concat(user.lastName));
            }
        });
        return map;
    }, [data]);
    /** The user's display name, or null when the id is unknown, missing or unresolvable. */
    var getUserName = useCallback(function (id) {
        var _a;
        if (!id)
            return null;
        if (SYSTEM_USER_IDS[id])
            return SYSTEM_USER_IDS[id];
        return (_a = namesById.get(id)) !== null && _a !== void 0 ? _a : null;
    }, [namesById]);
    return { getUserName: getUserName, isLoading: idsToFetch.length > 0 && isLoading };
};
export default useUserNames;
